// =============================================================================
// Avatar Component
// =============================================================================
// Displays a user avatar with image, initials fallback, and online indicator.
// =============================================================================

import { type ImgHTMLAttributes, forwardRef, useState } from 'react';
import { clsx } from 'clsx';

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export interface AvatarProps
  extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'alt' | 'children'> {
  /** Image URL. Falls back to initials if not provided or fails to load. */
  src?: string | null;
  /** Full name used to generate initials. */
  name?: string | null;
  /** Size variant. */
  size?: AvatarSize;
  /** Show a green online indicator dot. */
  online?: boolean;
}

const sizeStyles: Record<AvatarSize, string> = {
  xs: 'h-6 w-6 text-[10px]',
  sm: 'h-8 w-8 text-xs',
  md: 'h-10 w-10 text-sm',
  lg: 'h-12 w-12 text-base',
  xl: 'h-16 w-16 text-lg',
};

const indicatorSizes: Record<AvatarSize, string> = {
  xs: 'h-1.5 w-1.5 ring-1',
  sm: 'h-2 w-2 ring-[1.5px]',
  md: 'h-2.5 w-2.5 ring-2',
  lg: 'h-3 w-3 ring-2',
  xl: 'h-3.5 w-3.5 ring-2',
};

/** Generate 1-2 letter initials from a name string. */
function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** Deterministic background color from a name string. */
function getColorFromName(name: string): string {
  const colors = [
    'bg-brand-100 text-brand-700',
    'bg-emerald-100 text-emerald-700',
    'bg-amber-100 text-amber-700',
    'bg-rose-100 text-rose-700',
    'bg-violet-100 text-violet-700',
    'bg-sky-100 text-sky-700',
    'bg-orange-100 text-orange-700',
    'bg-teal-100 text-teal-700',
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
}

export const Avatar = forwardRef<HTMLDivElement, AvatarProps>(
  ({ src, name, size = 'md', online, className, ...props }, ref) => {
    const [imgError, setImgError] = useState(false);
    const showImage = src && !imgError;
    const initials = name ? getInitials(name) : '?';
    const colorClass = name ? getColorFromName(name) : 'bg-surface-tertiary text-text-secondary';

    return (
      <div ref={ref} className={clsx('relative inline-flex shrink-0', className)}>
        {showImage ? (
          <img
            src={src}
            alt={name ?? 'Avatar'}
            onError={() => setImgError(true)}
            className={clsx(
              'rounded-full object-contain',
              sizeStyles[size],
            )}
            {...props}
          />
        ) : (
          <div
            className={clsx(
              'inline-flex items-center justify-center rounded-full font-semibold',
              sizeStyles[size],
              colorClass,
            )}
            aria-label={name ?? 'Avatar'}
          >
            {initials}
          </div>
        )}

        {/* Online indicator */}
        {online && (
          <span
            className={clsx(
              'absolute bottom-0 right-0 rounded-full bg-success ring-surface',
              indicatorSizes[size],
            )}
          />
        )}
      </div>
    );
  },
);

Avatar.displayName = 'Avatar';
